/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Modal,
  Input,
  Typography,
  Card,
  Row,
} from 'antd';
import { useSelector } from 'react-redux';
import { onError } from 'utils/handlers';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import { getTiposDeDato } from 'api/configuracion';
import {
  deleteVariable,
  getVariables,
  patchVariable,
  postVariable,
  baseURI,
} from 'api/configuracion/variables';
import keyGenerator from 'utils/keyGenerator';
import { noSpaces, toInteger } from 'utils/normalizers';
import { uniqueValidator } from 'utils/rules';
import ValoresParaVariablesDeSeleccion, {
  permissionValorPorVariableDeSeleccion,
} from 'components/Catalogos/ValoresParaVariablesDeSeleccion';
import { permissionPropTypes } from 'utils/permission/propTypes';
import { tiposDatosEnum, tiposDatosFormulaEnum } from 'views/Tramites/GPM/enums';

export const permissionVariables = {
  title: 'Variables',
  model: 'variable',
  catalogs: [permissionValorPorVariableDeSeleccion.model],
};

const Variables = ({
  callback,
  disabled,
  permission,
}) => {
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const _tiposDeDato = useSelector(({ catalogs }) => catalogs.tiposDeDato);
  const [form] = Form.useForm();
  const es_para_formula = Form.useWatch('es_para_formula', form);
  const tipo_de_variable = Form.useWatch('tipo_de_variable', form);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);

  const tiposDeDato = _tiposDeDato.filter((e) => e.es_para_formula === es_para_formula);

  const fetchData = async () => {
    setLoading(true);
    const _variables = await getVariables();
    setData(_variables);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      await getTiposDeDato();
      const _variables = await getVariables();
      if (mounted) {
        setData(_variables);
        setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const response = await (key ? patchVariable(key, values) : postVariable(values));
      if (response) {
        onCancel();
        callback(response);
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const tipoDeDato = _tiposDeDato.find((e) => e.id === record.tipo_de_variable);
    form.setFieldsValue({
      ...record,
      es_para_formula: tipoDeDato.es_para_formula,
    });
  };

  const onClickAdd = () => {
    onCancel();
    keyGenerator(data, form, { keyProp: 'identificador' });
    setVisible(true);
  };

  const onClickDelete = () => setVisibleAlert(true);

  const onClickEdit = () => setVisible(true);

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteVariable(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    nombre: [
      requiredRule,
      uniqueValidator(data, selectedRowKeys[0], 'Nombre'),
    ],
    descripcion: [
      requiredRule,
      uniqueValidator(data, selectedRowKeys[0], 'Descripción'),
    ],
  };

  const columns = [
    {
      titleText: 'Identificador',
      key: 'identificador',
      dataIndex: 'identificador',
    },
    {
      titleText: 'Nombre',
      key: 'nombre_de_variable',
      dataIndex: 'nombre_de_variable',
    },
    {
      titleText: 'Descripción',
      key: 'descripcion_de_variable',
      dataIndex: 'descripcion_de_variable',
    },
    {
      titleText: 'Tipo de Variable',
      key: 'tipo_de_variable',
      dataIndex: 'tipo_de_variable',
      render: (val) => _tiposDeDato.find((e) => e.id === val)?.descripcion
        .concat([tiposDatosFormulaEnum.DECIMAL, tiposDatosEnum.DECIMAL].includes(val) ? `(${val.decimales})` : ''),
    },
    {
      titleText: 'Es Script',
      key: 'es_script',
      dataIndex: 'es_script',
      render: (_, record) => (_tiposDeDato.find((e) => e.id === record.tipo_de_variable)
        ?.es_para_formula ? 'Si' : 'No'),
    },
  ];

  const editingList = visible && selectedRowKeys.length && [12, 13].includes(tipo_de_variable);
  const isDecimal = [tiposDatosFormulaEnum.DECIMAL, tiposDatosEnum.DECIMAL]
    .includes(tipo_de_variable);

  // eslint-disable-next-line react/prop-types
  const Formulario = ({ modal }) => {
    let colProps;
    if (modal) {
      colProps = {
        span: 24,
      };
    } else {
      colProps = {
        xs: 24,
        sm: 24,
        md: 8,
      };
    }
    return (
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ estados_globales: 1, es_para_formula: true }}
        name="variables"
      >
        <Row gutter={10}>
          <Col {...colProps}>
            <Form.Item
              name="identificador"
              rules={rules.required}
              label="Identificador"
            >
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item
              name="nombre_de_variable"
              rules={rules.nombre}
              label="Nombre"
              normalize={(val = '') => noSpaces(val.replace(/^\d+/g, '').replace(/[^\d\w_]+/gi, ''))}
            >
              <Input allowClear autoComplete="off" />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item
              name="descripcion_de_variable"
              rules={rules.descripcion}
              label="Descripción"
            >
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item
              name="es_para_formula"
              rules={rules.required}
              label="¿Es para fórmula?"
            >
              <Select
                trueFalse
                onChange={() => form.resetFields(['tipo_de_variable'])}
                disabled={editingList}
              />
            </Form.Item>
          </Col>
          <Col {...colProps}>
            <Form.Item
              name="tipo_de_variable"
              rules={rules.required}
              label="Tipo de Variable"
            >
              <Select disabled={editingList} dataSource={tiposDeDato} />
            </Form.Item>
          </Col>
          {isDecimal && (
            <Col {...colProps}>
              <Form.Item
                name="decimales"
                rules={rules.required}
                label="Decimales"
                normalize={(val) => toInteger(val)}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
          <Col {...colProps}>
            <Form.Item
              name="estados_globales"
              rules={rules.required}
              label="Estado"
              hasFeedback
            >
              <Select
                disabled={!selectedRowKeys.length}
                dataSource={estadosGlobales}
              />
            </Form.Item>
          </Col>
          {modal && [12, 13].includes(tipo_de_variable) && (
            <Typography.Text>
              Una vez guardada la variable podrá registrar los valores para la selección
            </Typography.Text>
          )}
          {!modal && [12, 13].includes(tipo_de_variable) && (
            <ValoresParaVariablesDeSeleccion
              variable={selectedRowKeys[0]}
              permission={permission.catalogs[permissionValorPorVariableDeSeleccion.model]}
            />
          )}
        </Row>
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>
        <Form.Item hidden>
          <Button htmlType="submit" />
        </Form.Item>
      </Form>
    );
  };

  return (
    <>
      <Spin tip="Cargando..." spinning={loading}>
        <br />
        {!editingList && (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickDelete,
              onClickEdit,
            }}
            noControls={disabled}
            allowSort={false}
            allowSearch={false}
            rowKey="id"
            baseURI={baseURI}
            allowImport
            permission={{ ...permission, import: true }}
            hasAllViewPermission
          />
        )}
        {editingList && (
          <Card
            className="form-container"
            title="Editar Variable"
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                permission={permission}
                loading={loading}
              />
            )}
            bordered={false}
          >
            <Formulario />
          </Card>
        )}
        <Modal
          visible={visible && !editingList}
          closable={false}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Variable`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              permission={{ ...permission, import: true }}
            />
          )}
          keyboard={!loading}
          maskClosable={!loading}
          onCancel={onCancel}
          forceRender
        >
          <Col span={24}>
            <Formulario modal />
          </Col>
        </Modal>
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="la variable"
      />
    </>
  );
};

Variables.propTypes = {
  callback: PropTypes.func,
  disabled: PropTypes.bool,
  permission: permissionPropTypes.isRequired,
};

Variables.defaultProps = {
  callback: () => { },
  disabled: false,
};

export default Variables;
